import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom'; // Import Link for navigation

const BlogSection = () => {
  const [blogs, setBlogs] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [visibleStart, setVisibleStart] = useState(0);
  const [animatedCards, setAnimatedCards] = useState(new Set()); // This state is not currently used for animation, but kept as per original code
  const visibleCount = 3;

  // Define the API URL using process.env
  // For Canvas environment, process.env.REACT_APP_API_URL might not be directly available.
  // In a real React app, you would configure this via .env files.
  // For this example, we'll use a placeholder or assume it's set up externally.
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

  useEffect(() => {
    const fetchBlogs = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/blog`);
        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) {
            // Ensure each blog has a proper ID and add index as fallback
            const blogsWithIds = data.map((blog, index) => ({
              ...blog,
              // Use _id (MongoDB), id, or create one from index
              uniqueId: blog._id || blog.id || `blog-${index}`,
              // Ensure we have the proper ID field for routing
              blogId: blog._id || blog.id
            }));
            setBlogs(blogsWithIds);
          } else {
            setError("No blogs found in the database.");
            setBlogs([]);
          }
        } else {
          throw new Error(`Failed to fetch blogs. Status: ${response.status}`);
        }
      } catch (err) {
        console.error("Error fetching blogs from backend:", err);
        setError("Connection to threat intelligence database failed. Please check your connection.");
        setBlogs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, [API_URL]); // Dependency array includes API_URL to re-fetch if it changes

  // Function to scroll the blog cards left or right
  const scroll = (direction) => {
    if (direction === 'left') {
      setVisibleStart((prev) => Math.max(0, prev - 1));
    } else {
      setVisibleStart((prev) => Math.min(blogs.length - visibleCount, prev + 1));
    }
  };

  // Helper function to get Tailwind CSS text color based on threat level
  const getThreatColor = (threat) => {
    switch (threat?.toUpperCase()) {
      case 'CRITICAL': return 'text-red-600'; // Darker for white background
      case 'HIGH': return 'text-orange-600'; // Darker for white background
      case 'MEDIUM': return 'text-yellow-600'; // Darker for white background
      case 'LOW': return 'text-green-600'; // Darker for white background
      default: return 'text-[#00aacc]'; // Adjusted default for better contrast on white
    }
  };

  // Helper function to get Tailwind CSS background and border color based on threat level
  const getThreatBg = (threat) => {
    switch (threat?.toUpperCase()) {
      case 'CRITICAL': return 'bg-red-100 border-red-200'; // Lighter for white background
      case 'HIGH': return 'bg-orange-100 border-orange-200'; // Lighter for white background
      case 'MEDIUM': return 'bg-yellow-100 border-yellow-200'; // Lighter for white background
      case 'LOW': return 'bg-green-100 border-green-200'; // Lighter for white background
      default: return 'bg-[#e0f7fa] border-[#b2ebf2]'; // Lighter default for white background
    }
  };

  // Function to handle card hover (currently just adds to a set, no visible animation tied to it)
  const handleCardHover = (blogId) => {
    setAnimatedCards(prev => new Set([...prev, blogId]));
  };

  return (
    <section className="relative min-h-screen bg-white text-gray-900 font-sans overflow-hidden">
      {/* Animated Background Elements - Removed for a cleaner, white UI */}
      {/* Main Content with subtle glow */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-12 main-content-subtle-glow">
        
        {/* Header Section */}
        <div className="text-center mb-12 animate-fade-in-up">
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="w-20 h-20 bg-[#00ffff]/10 rounded-full flex items-center justify-center animate-pulse-glow">
                <svg className="w-10 h-10 text-[#00ffff] animate-rotate-slow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div className="absolute -top-2 -right-2 w-6 h-6 bg-[#00ffff] rounded-full flex items-center justify-center animate-bounce">
                <span className="text-xs font-bold text-black">!</span>
              </div>
              {/* Orbiting dots */}
              <div className="absolute inset-0 animate-spin-slow">
                <div className="absolute -top-4 left-1/2 w-2 h-2 bg-[#00ffff] rounded-full animate-pulse"></div>
                <div className="absolute -right-4 top-1/2 w-2 h-2 bg-[#ff00ff] rounded-full animate-pulse animation-delay-500"></div>
                <div className="absolute -bottom-4 left-1/2 w-2 h-2 bg-[#00ffff] rounded-full animate-pulse animation-delay-1000"></div>
                <div className="absolute -left-4 top-1/2 w-2 h-2 bg-[#ff00ff] rounded-full animate-pulse animation-delay-1500"></div>
              </div>
            </div>
          </div>
          
          <h1 className="text-4xl lg:text-6xl font-black mb-4 leading-tight">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00ffff] to-[#ff00ff] animate-text-glow">
              GLOCYBS
            </span>
            <br />
            <span className="text-gray-900">CYBER INTELLIGENCE</span>
          </h1>
          
          <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto">
            Stay ahead of evolving threats with our latest 
            <span className="text-[#00aacc] font-bold animate-pulse"> security insights</span> and 
            <span className="text-[#cc00cc] font-bold animate-pulse"> threat intelligence</span>
          </p>
          
          {/* Animated Status Bar */}
          <div className="max-w-md mx-auto bg-gray-100 rounded-full border border-gray-300 p-2 shadow-sm">
            <div className="flex items-center justify-between px-4 py-2">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs text-green-600 font-medium">LIVE FEED ACTIVE</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-700">THREAT LEVEL:</span>
                <span className="text-xs text-orange-600 font-bold">ELEVATED</span>
              </div>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-flex items-center gap-3">
              <div className="w-8 h-8 border-2 border-[#00aacc] border-t-transparent rounded-full animate-spin"></div>
              <span className="text-[#00aacc] font-medium">ACCESSING THREAT DATABASE...</span>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-100 border border-red-300 rounded-lg p-4 mb-8 animate-fade-in shadow-md">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-red-600 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-red-600 font-medium">{error}</span>
            </div>
          </div>
        )}

        {/* Blog Navigation */}
        {!loading && blogs.length > 0 && (
          <div className="flex items-center justify-between mb-8 animate-fade-in">
            <button
              onClick={() => scroll('left')}
              disabled={visibleStart === 0}
              className={`group px-6 py-3 rounded-lg border transition-all duration-300 ${
                visibleStart === 0 
                  ? 'bg-gray-200 border-gray-300 text-gray-500 cursor-not-allowed' 
                  : 'bg-[#00ffff]/10 border-[#00ffff]/20 text-[#00aacc] hover:bg-[#00ffff]/20 hover:shadow-[#00ffff]/10 hover:shadow-lg transform hover:scale-105'
              }`}
            >
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="font-medium">PREVIOUS</span>
              </div>
            </button>
            
            <div className="flex items-center gap-4 bg-gray-100 rounded-full px-6 py-3 border border-gray-300 shadow-sm">
              <span className="text-gray-700 font-bold text-sm">SHOWING</span>
              <span className="text-gray-900 font-bold">{visibleStart + 1}-{Math.min(visibleStart + visibleCount, blogs.length)}</span>
              <span className="text-gray-600">OF</span>
              <span className="text-gray-900 font-bold">{blogs.length}</span>
            </div>
            
            <button
              onClick={() => scroll('right')}
              disabled={visibleStart >= blogs.length - visibleCount}
              className={`group px-6 py-3 rounded-lg border transition-all duration-300 ${
                visibleStart >= blogs.length - visibleCount
                  ? 'bg-gray-200 border-gray-300 text-gray-500 cursor-not-allowed' 
                  : 'bg-[#00ffff]/10 border-[#00ffff]/20 text-[#00aacc] hover:bg-[#00ffff]/20 hover:shadow-[#00ffff]/10 hover:shadow-lg transform hover:scale-105'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="font-medium">NEXT</span>
                <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>
          </div>
        )}

        {/* Blog Grid */}
        {!loading && blogs.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {blogs.slice(visibleStart, visibleStart + visibleCount).map((blog, index) => (
              <div 
                key={blog.uniqueId} 
                className="group bg-white rounded-2xl border border-gray-200 overflow-hidden hover:border-[#00aacc] transition-all duration-500 transform hover:scale-105 hover:shadow-xl hover:shadow-[#00aacc]/20 animate-fade-in-up cursor-pointer"
                style={{animationDelay: `${index * 200}ms`}}
                onMouseEnter={() => handleCardHover(blog.uniqueId)}
              >
                
                {/* Blog Image */}
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={blog.iconUrl || blog.image || 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=400&h=250&fit=crop'} 
                    alt={blog.heading || blog.title} 
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-white/80 to-transparent"></div>
                  
                  {/* Threat Level Badge */}
                  {blog.threat_level && (
                    <div className={`absolute top-4 right-4 px-3 py-1 rounded-full border ${getThreatBg(blog.threat_level)} animate-pulse`}>
                      <span className={`text-xs font-bold ${getThreatColor(blog.threat_level)}`}>
                        {blog.threat_level}
                      </span>
                    </div>
                  )}
                  
                  {/* Overlay Animation */}
                  <div className="absolute inset-0 bg-[#00ffff]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>

                {/* Blog Content */}
                <div className="p-6">
                  {/* Category Badge */}
                  {blog.category && (
                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#00ffff]/10 border border-[#00ffff]/20 rounded-full mb-4">
                      <div className="w-2 h-2 bg-[#00ffff] rounded-full animate-pulse"></div>
                      <span className="text-xs text-[#00aacc] font-medium">{blog.category}</span>
                    </div>
                  )}
                  
                  <h3 className="text-xl font-bold text-[#00aacc] mb-3 leading-tight group-hover:text-gray-900 transition-colors duration-300">
                    {blog.heading || blog.title}
                  </h3>
                  
                  <p className="text-gray-700 text-sm mb-4 line-clamp-3 group-hover:text-gray-800 transition-colors duration-300">
                    {blog.content || blog.description}
                  </p>

                  {/* Meta Information */}
                  <div className="flex items-center justify-between text-xs text-gray-600 mb-4">
                    <span>{blog.readTime || '5 min read'}</span>
                    <span>{blog.author || 'Glocybs Team'}</span>
                  </div>

                  {/* Read More Button - Fixed to handle undefined IDs */}
                  {blog.blogId ? (
                    <Link 
                      to={`/blog/${blog.blogId}`}
                      className="group/btn inline-flex items-center gap-2 text-[#00aacc] hover:text-gray-900 transition-all duration-300 font-medium"
                    >
                      <span>ACCESS FULL REPORT</span>
                      <svg className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </Link>
                  ) : (
                    <div className="inline-flex items-center gap-2 text-gray-500 font-medium cursor-not-allowed">
                      <span>ID NOT AVAILABLE</span>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* No blogs message */}
        {!loading && blogs.length === 0 && !error && (
          <div className="text-center py-12">
            <div className="bg-yellow-100 border border-yellow-300 rounded-lg p-6 animate-fade-in shadow-md">
              <div className="flex items-center justify-center gap-2 mb-4">
                <svg className="w-7 h-7 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-yellow-600 text-xl font-bold">NO INTELLIGENCE REPORTS FOUND</span>
              </div>
              <p className="text-yellow-700 text-lg">
                No threat intelligence reports are currently available. Please check back later or contact our team for more information.
              </p>
            </div>
          </div>
        )}

        {/* View All Button */}
        {!loading && blogs.length > 0 && (
          <div className="text-center animate-fade-in">
            <Link 
              to="/blogs"
              className="group inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-[#00ffff] to-[#00aacc] text-black font-bold rounded-xl hover:from-[#00aacc] hover:to-[#00ffff] transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-[#00ffff]/50"
            >
              <svg className="w-5 h-5 transition-transform group-hover:rotate-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
              </svg>
              ACCESS ALL INTELLIGENCE
              <div className="w-2 h-2 bg-black/50 rounded-full animate-pulse"></div>
            </Link>
          </div>
        )}
      </div>

      {/* Custom Styles */}
      <style jsx>{`
        @keyframes fade-in-up {
          from { 
            opacity: 0; 
            transform: translateY(30px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 20px rgba(0, 255, 255, 0.3); }
          50% { box-shadow: 0 0 40px rgba(0, 255, 255, 0.6); }
        }
        
        @keyframes rotate-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes text-glow {
          0%, 100% { text-shadow: 0 0 10px rgba(0, 255, 255, 0.3); } /* Adjusted for white background */
          50% { text-shadow: 0 0 20px rgba(0, 255, 255, 0.5); } /* Adjusted for white background */
        }
        
        /* Removed grid-slide and scan-lines for a cleaner UI */

        /* Main content subtle glow - Adjusted for white background */
        .main-content-subtle-glow {
          position: relative;
          z-index: 10;
          box-shadow: 
            0 0 5px rgba(0, 255, 255, 0.1), 
            0 0 10px rgba(0, 255, 255, 0.05);
          animation: subtle-scan-glow 4s ease-in-out infinite alternate;
        }

        @keyframes subtle-scan-glow {
          0% {
            box-shadow: 
              0 0 5px rgba(0, 255, 255, 0.1), 
              0 0 10px rgba(0, 255, 255, 0.05);
          }
          50% {
            box-shadow: 
              0 0 10px rgba(0, 255, 255, 0.2), 
              0 0 20px rgba(0, 255, 255, 0.1);
          }
          100% {
            box-shadow: 
              0 0 5px rgba(0, 255, 255, 0.1), 
              0 0 10px rgba(0, 255, 255, 0.05);
          }
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        .animate-pulse-glow {
          animation: pulse-glow 2s ease-in-out infinite;
        }
        
        .animate-rotate-slow {
          animation: rotate-slow 20s linear infinite;
        }
        
        .animate-spin-slow {
          animation: spin-slow 30s linear infinite;
        }
        
        .animate-text-glow {
          animation: text-glow 3s ease-in-out infinite;
        }
        
        /* Removed animation-delay classes if not used after removing elements */
        .animation-delay-500 {
          animation-delay: 500ms;
        }
        
        .animation-delay-1000 {
          animation-delay: 1000ms;
        }
        
        .animation-delay-1500 {
          animation-delay: 1500ms;
        }
        
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        /* Removed bg-grid-pattern, scan-line-1, scan-line-2, scan-horizontal, scan-vertical */
      `}</style>
    </section>
  );
};

export default BlogSection;
